import { useCallback, useEffect, useMemo } from "react";
import { filterStore, pageStore, locationStore, mapStore, userStore } from "state/store-zustand";

import { searchQueryToParams, paramsToSearchQuery, updateNavItemsWithCityAndTheme } from "components/utils/listUtils";

// TODO: Load from design tokens
const blue = "#007acc"
const darkblue = "#004175"
const lightblue = "#e6f4ff"
const green = "#55BA47"
const darkgreen = "#3D8B3D"
const lightgray = "#f6f6ff"
const pink = "#ff1f78"
const lightpurple = 'hsl(253, 74%, 68%)'
const darkpurple = 'hsl(253, 74%, 68%)'

// TODO: Make a hook that updates these based on the theme
const numColor = darkblue
const numTheme = 'dark' // 'dark' or 'light'
const numTextColor = numTheme == 'dark' ? '#fff' : '#111'
const numBgColor = numColor
  ? numColor
  : numTheme == 'dark'
    ? '#111'
    : '#fff'

const numBorderColor = numTheme == 'dark' ? '#fff' : '#CCC'


// TODO: placeholder for this coming from the API
const themes = {
  default: {
    accent: "#332F36",
    foreground: "#222222",
    background: "#FFFFFF",
    color: "#000000",
    numTextColor: numTextColor,
    numBadgeSize: 24,
    numBgColor: numBgColor,
    numBorderColor: numBorderColor,
    numTheme: numTheme,
    businessForm: {
      title: 'Add Your Business',
      message: 'Search to add or update a place or event to Vibemap. Send any questions or suggestions. <a href="mailto:businesses@vibemap.com">businesses@vibemap.com</a>',
    },
    eventForm: {
      title: 'Add Event',
      message: 'Search to add or update a place or event to Vibemap. Send any questions or suggestions. <a href="mailto:info@vibemap.com"> info@vibemap.com</a>',
      showVibePoints: true,
    },
    cards: {
      open_to_website: false,
      open_to_blank: false,
      open_to_website_from_card: false,
      open_with_params: true, // copy url params to the link
    },
    events: {
      // Can control the vibes, categories, and tags shown
      showOrdinal: true,
    },
    map: {
      boundary: {
        fill_color: lightpurple,
        fill_opacity: 0.1,
        line_color: darkblue,
        line_opacity: 0.8,
        line_weight: 4,
        line_dash_array: [3, 2], // [1.4, 1]
      },
      routeColor: "#9115CB",
    },
  },
  light: {
    foreground: "#FFFFFF",
    background: "#222222",
    color: "#000000",
  },
  clermont: {
    logo: 'https://discoverclermont.com/media/04wf0iin/discover_clermont_logo_rgb.jpg',
    cards: {
      open_to_website: true,
      open_to_page: (type, slug) => `https://discoverclermont.com/events/details?id=${slug}`,
      open_to_blank: false
    },
    events: {
      link: 'https://on.vibemap.com/events-clermont',
      clipToBounds: true,
    }
  },
  cleveland: {
    logo: 'https://images.squarespace-cdn.com/content/v1/645ba4265ac81f7c3a42f05d/3ee82205-2e06-4be7-8c13-1c84748839e4/DTCLE-Logo.png?format=300w',
    cards: {
      open_to_website: true
    },
    events: {
      link: 'https://on.vibemap.com/events_downtown_cleveland'
    }
  },
  downtown_tulsa: {
    cards: {
      open_to_website: true, // Better description is open to vibemap
      open_to_page: (type, slug) => `https://downtowntulsa.com/do/${slug}`
    },
    events: {
      link: 'https://on.vibemap.com/downtown-tulsa-events',
      link_newsletter: 'https://on.vibemap.com/downtown-tulsa-events-for-newsletter',
    },
    eventForm: {
      title: 'Add Event',
      message: 'Search for your event or enter the info below. Send any questions or suggestions. <a href="mailto:info@downtowntulsa.com">info@downtowntulsa.com</a>',
      showIntroMessage: false,
      showVibePoints: false,
    },
    logo: 'https://ctycms.com/ok-tulsa/tulsa-logo-600x400.svg'
  },
  downtown_portland: {
    cards: {
      open_to_website: true // Better description is open to vibemap
    },
    map: {
      boundary: {
        fill_color: '#E4A682', // Something old and brick like
        fill_opacity: 0.4,
        line_color: '#cf2e2e',
        line_opacity: 0.8,
        line_weight: 2,
        line_dash_array: [3, 2], // [1.4, 1]
      },
    }
  },
  evanston: {
    cards: {
      open_to_website: true
    },
    events: {
      link: 'https://on.vibemap.com/evanston-events',
      randomizeImages: true,
    },
    map: {
      //heightMobile: "65vh", // Not used
      showHeat: false,
      markerFeatured: {
        'background-color': '#007acc',
        'border-color': '#007acc',
        'latitude': 42.0511,
        'longitude': -87.6784,
        'name': null,
        'image': 'https://downtownevanston.org/sites/downtown-evanston/images/dte-logo.png',
        'style': 'marker', // Or tooltip
        'persistent': true,
      },
      showScrollArea: false,
      titleCards: {
        'Evanston_Trick_Treat': {
          id: "363f8183-aa56-4d85-99f1-1c2dd8727ab4",
          slug: "fountain-square-1",
          title: 'Downtown Evanston Trick or Treat',
          subtitle: 'Start Here!',
          description: '<div class="note">🎃Evanston Trick or Treat: Start Here and pick up your map.</div> Bring your kids downtown to trick or treating at local businesses! Stroll to participating downtown businesses for candy and other goodies. <br/> <strong>Note</strong>: that not all businesses are participating so look for spooky posters on the doors. Some businesses will have candy outside.',
          image: 'https://ik.imagekit.io/vibemap/evaston/Downtown%20Evanston%20%20Trick%20or%20Treat_nmIpzFIwO.jpg?updatedAt=1698274466089',
          geometry: {
            "type": "Point",
            "coordinates": [
              -87.6815944,
              42.047158
            ]
          },
          showAsCard: true,
          "vibes": [
            "family",
            "kids",
            "outdoors",
            "free"
          ]
        }
      }
    }
  },
  "experience-montclair": {
    cards: {
      open_to_website: true,
      open_to_page: (type, slug) => `https://experiencemontclair.org/${type ? type : 'event'}-details/?id=${slug ? slug : ''}`  ,
      use_dynamic_link: true
    },
  },
  harlem: {
    cards: {
      open_to_website: true // Better description is open to vibemap
    },
    events: {
      link: 'https://on.vibemap.com/125th-harlem-events'
    },
    map: {
      boundary: {
        fill_color: 'hsl(253, 74%, 68%)',
        fill_opacity: 0.2,
        line_color: darkblue,
        line_opacity: 0.8,
        line_weight: 3,
        line_dash_array: [4, 0], // [1.4, 1]
      }
    },
  },
  "ivcba": {
    cards: {
      open_to_website: true,
      open_to_blank: true, // Open links in a new tab
    }
  },
  "king-of-prussia": {
    cards: {
      open_to_website: true,
      open_to_page: (type, slug) => `https://visitkop.com/event-landing?id=${slug}`,
      use_dynamic_link: true
    },
    events: {
      showOrdinal: false,
      spaceBetweenAM: true, // Show space between AM and PM in time
    },
    logo: 'data:image/png;base64,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',
  },
  "la-jolla": {
    cards: {
      open_to_website: true,
      open_to_page: (type, slug) => `https://lajollabythesea.com/event-details/?id=${slug ? slug : ''}`,
    },
    events: {
      randomizeImages: true,
      relatedEvents: true,
      relatedEventsRadius: 0.5,
    },
    map: {
      boundary: {
        fill_color: '#ff5d37',
        fill_opacity: 0.08,
        line_color: '#ff5d37',
        line_opacity: 0.6,
        line_weight: 3,
        line_dash_array: [2, 1], // [1.4, 1]
      },
    },
  },
  "lynchburg-downtown": {
    cards: {
      open_to_website: true,
      open_to_page: (type, slug) => `https://downtownlynchburg.com/${type ? type : 'event'}-details/?id=${slug ? slug : ''}`,
      use_dynamic_link: true
    },
    map: {
      boundary: {
        fill_color: '#801643',
        fill_opacity: 0.08,
        line_color: '#801643',
        line_opacity: 0.6,
        line_weight: 3,
        line_dash_array: [2, 1], // [1.4, 1]
      },
      routeColor: "#9115CB",
      showAddressAbove: true,
    },
  },
  moline: {
    logo: 'https://img1.wsimg.com/isteam/ip/cab77d89-366b-4bd5-ab46-461958fdf5c8/blob-3a940c1.png/:/cr=t:0%25,l:0%25,w:100%25,h:100%25/rs=w:556,h:200,cg:true',
    cards: {
      open_to_website: true,
      open_to_blank: true,
    },
  },
  "montclair": {
    logo: 'https://www.downtownpittsburgh.com/wp-content/uploads/2021/02/Downtown-Pittsburgh-Logo-Color.png',
    cards: {
      open_to_website: true
    },
    events: {
      link: 'https://on.vibemap.com/events-downtown-pittsburgh'
    }
  },
  "nemaa ": {

  },
  "oakland-districts": {
    map: {
      boundary: {
        fill_opacity: 0.2,
        label_size: 24,
        line_color: "#000000",
        line_opacity: 0.6,
        line_weight: 2,
        show_labels: true,
      }
    }
  },
  "oakland-downtown": {
    cards: {
      open_to_website: true,
      open_to_website_from_card: false,
      open_to_page: (type, slug) => `https://www.oaklandcentral.com/${type ? type : 'event'}-details/?id=${slug ? slug : ''}&theme=oakland-downtown`,
      use_dynamic_link: true
    },
    businessForm: {
      badge: 'oww',
      title: 'Add Your Business',
      offer_icon: 'https://vibemap.wpengine.com/wp-content/uploads/2025/01/oww.png',
      offer_message: 'Add your business to the Oakland Works Wednesday program. Do you have a special offer for the community?',
      offer_show: true,
      offer_fields: ['description'],
      message: 'Search to add or update a place or event to Vibemap. Send any questions or suggestions. <a href="mailto:info@vibemap.com"> info@vibemap.com</a>',
      showVibePoints: true,
    },

    filters: {
      tagsToExclude: ['Oakland_Attractions', 'Oakland_Fairyland', 'Oakland_Museum', 'OAKRestaurantWeek2024', 'Play']
    },
    map: {
      boundary: {
        fill_opacity: 0.2,
        fill_color: [
          "match",
          ["get", "name"],
          ["Chinatown", "Chinatown-Laney"],
          "#c8662e",
          ["Lakeside"],
          "#56c2ab",
          ["Uptown"],
          "#2d8cc0",
          ["", "Downtown"],
          "#57b47e",
          ["Old Oakland"],
          "#c0c01b",
          ["Jack London "],
          "#d49426",
          ["Jack London Square"],
          "#f9c89a",
          "#f2e2a1"
        ],
        line_color: "#000000",
        line_opacity: 0.2,
        line_weight: 1,
        show_labels: true,
      }
    }
  },
  peoria: {
    foreground: "#FFFFFF",
    background: "#222222",
    color: "#000000",
    cards: {
      open_to_website: false,
      open_with_params: false, // copy url params to the link
    },
    filters: {
      tagsToExclude: ['Play_in_Peoria', 'Default']
    },
    events: {
      showOnlyEventTags: false,
    },
    eventForm: {
      title: 'Add Event',
      link: 'https://peoria.org/events/submit-your-event',
      message: 'Search to add or update a place or event to Vibemap. Send any questions or suggestions. <a href="mailto:info@vibemap.com"> info@vibemap.com</a>',
      showIntroMessage: false,
      showVibePoints: false,
    },
    showLogin: false,
  },
  pittsburgh: {
    logo: 'https://www.downtownpittsburgh.com/wp-content/uploads/2021/02/Downtown-Pittsburgh-Logo-Color.png',
    cards: {
      open_to_website: true
    },
    events: {
      link: 'https://on.vibemap.com/events-downtown-pittsburgh'
    },
    map: {
      boundary: {
        fill_color: '#FFEE79',
        fill_opacity: 0.2,
        line_color: '#FF8C00',
        line_opacity: 0.8,
        line_weight: 2,
        line_dash_array: [3, 2], // [1.4, 1]
      },
    }
  },
  riverside: {
    accent: "#ed1c80",
    numBadgeSize: 24,
    numBgColor: "#ed1c80",
    numTextColor: "#FFF",
    cards: {
      open_to_website: true
    },
    map: {
      boundary: {
        fill_color: lightgray,
        fill_opacity: 0.1,
        line_color: darkblue,
        line_opacity: 0.8,
        line_weight: 4,
        line_dash_array: [3, 2], // [1.4, 1]
      },
      routeColor: "#ed1c80",
      setShowCardNum: false,
    }
  },
  shout: {

  },
  soma_west: {
    events: {
      vibes: ['art', 'cultural', 'free', 'family', 'food', 'music', 'dance']
    }
  },
  springfield: {
    map: {
      boundary: {
        fill_opacity: 0.0,
        line_opacity: 0.0,
      }
    }
  },
  toledo: {
    logo: 'https://www.downtowntoledo.org/media/384259/downtowntoledologo_vertical_rgb.jpg',
    cards: {
      open_to_website: true
    },
    events: {
      link: 'https://on.vibemap.com/events-toledo'
    }
  },
  tucson: {
    map: {
      shouldIncludeTags: true,
      heightOffset: -200,
    }
  },
  union_square: {
    cards: {
      open_to_website: true,
      open_to_page: (type, slug) => `https://www.unionsquarenyc.org/${type ? type : 'event'}-details/?id=${slug ? slug : ''}`,
      use_dynamic_link: true
    },
    events: {
      vibes: ['art', 'free', 'family', 'food', 'music', 'dance', 'educational', 'business']
    },
    filters: {
      tagsToInclude: ['Design', 'Company', 'Family', 'Health & Wellness', 'Thrift'],
      tagsToExclude: ['union_square', 'Home Decor', 'Design agency'],
      tagsToIncludeOnly: true,
      tagNames: {
        'Design': 'Home Goods + Design',
        'Company': 'Tech + Innovation',
        'Health & Wellness': 'Lifestyle + Fitness',
        'Thrift': 'Vintage + Thrift',
        'Family': 'Family-Friendly',
      }
    },
    map: {
      boundary: {
        fill_color: "#99DBE4",
        fill_opacity: 0.2,
        line_color: "#29737E",
        line_opacity: 0.6,
        line_weight: 4,
        line_dash_array: [3, 2], // [1.4, 1]
      },
      categoryColors: {
        'art': 'blue',
        'health & wellness': 'green',
        'cultural': 'pink',
        'dance': 'green',
        'design': 'blue',
        'home decor': 'blue',
        'thrift': 'purple',
      },
    }
  },
  westshore: {
    logo: 'https://www.choosewestshore.com/wp-content/themes/westshore-alliance/dist/img/logo.png',
    events: {
      link: 'https://on.vibemap.com/westshore-events',
      eventLayout: 'columns',
      tagAuto: ['Westshore Tampa'],
    },
    filters: {
      tagsToExclude: [
        'American', 'lunch',
        'tampa_places', 'westshore', 'bar', 'stores', 'event_venue', 'event rentals',
        'Fishing', 'dog park', 'hiking', 'market', 'nature trails', 'shopping centers', 'lounge']
    },
    map: {
      titleCards: {
        'westshore_art': {
          id: "363f8183-aa56-4d85-99f1-1c2dd8727ab4",
          slug: "fountain-square-1",
          title: 'Westshore Tampa Public Art',
          subtitle: 'Start Here!',
          description: 'This map showcases artists who are expressing the culture, history, and beauty of Westshore Tampa is eyecatching, murals and large-format artwork for all the public to enjoy.',
          image: 'https://ik.imagekit.io/vibemap/evaston/Downtown%20Evanston%20%20Trick%20or%20Treat_nmIpzFIwO.jpg?updatedAt=1698274466089',
          geometry: {
            "type": "Point",
            "coordinates": [
              -87.6815944,
              42.047158
            ]
          },
          showAsCard: false,
          "vibes": [
            "family",
            "kids",
            "outdoors",
            "free"
          ]
        }
      }
    }
  },
  westwood: {
    logo: 'https://thewestwoodvillage.com/wp-content/themes/westwood-village/images/common/logo-header-v2.png',
    cards: {
      open_to_website: true
    },
    events: {
      link: 'https://on.vibemap.com/westwood_village_events',
      randomizeImages: true,
      showFeaturedFirst: true,
    }
  },
  "yerba-buena": {
    logo: 'https://ctycms.com/ca-yerba-buena/yb560x205v2.svg',
    businessForm: {
      title: 'Add or Edit Your Business',
      message: 'Search to add or update a business listing on yerbabuena.org. Send any questions or suggestions to <a href="mailto:info@yerbabuena.org">info@yerbabuena.org</a>.'
    },
    eventForm: {
      showIntroMessage: false,
      showVibePoints: false,
    },
    places: {
      tagAuto: ['YBCBD_Businesses'],
    },
    map: {
      boundary: {
        fill_color: '#FFEE79',
        fill_opacity: 0.2,
        line_color: '#FF8C00',
        line_opacity: 0.8,
        line_weight: 2,
        line_dash_array: [3, 2], // [1.4, 1]
      },
      link: 'https://on.vibemap.com/yerba-buena-directory',
      routeColor: "#FF8C00",
    }
  },
}


export const loadFonts = (themeName) => {

  const appendLinkElement = (attributes) => {
    const link = document.createElement('link');
    Object.entries(attributes).forEach(([key, value]) => {
      link[key] = value;
    });
    document.head.appendChild(link);
  };

  // Preconnect links for all themes
  const preconnectLinks = [
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  ];

  preconnectLinks.forEach(appendLinkElement);


  // Theme-specific font links
  const themeFonts = {
    'culinaria': [
      'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,100..900;1,100..900&display=swap'
    ],
    'ivcba': [
      'https://use.typekit.net/wrw8eoz.css',
      'https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400..700;1,400..700&family=Raleway:ital,wght@0,100..900;1,100..900&display=swap'
    ],
    'king-of-prussia': [
      'https://vibemap.sfo3.cdn.digitaloceanspaces.com/pprightgrotesk-medium-webfont.woff2',
    ],
    'la-jolla': [
      'https://fonts.googleapis.com/css2?family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&display=swap'
    ],
    'lynchburg-downtown': [
      'https://use.typekit.net/vnc5qwg.css'
    ],
    'moline': [
      'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap'
    ],
    'oakland-downtown': [
      'https://fonts.googleapis.com/css2?family=Permanent+Marker&display=swap'
    ],
    'westwood': [
      'https://fonts.googleapis.com/css2?family=Righteous&family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap'
    ],
    'federal-hill': [
      'https://fonts.googleapis.com/css2?family=Roboto+Slab%3A400%2C%7CLexend+Peta%3A500%2C%2C600&display=fallback'
    ],
    'pittsburgh': [
      'https://fonts.googleapis.com/css2?family=Gilda+Display&family=Heebo:wght@100..900&display=swap'
    ],
    'union_square': [
      'https://static1.squarespace.com/static/6446ea35f719de478c377bb1/t/67daed4b0608f24bf1d408bb/1742400843369/NaNSerfAText-Book.woff2',
      'https://static1.squarespace.com/static/6446ea35f719de478c377bb1/t/67f411544405463711e71b45/1744048468138/NaNHoloX-Condensed-Bold.woff2',
      'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400..800;1,400..800&family=Lora:ital,wght@0,400..700;1,400..700&family=Raleway:ital,wght@0,100..900;1,100..900&display=swap'
    ],
    'yerba-buena': [
      'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=K2D:ital,wght@0,400;0,500;0,600;0,700;0,800&display=swap'
    ]
  }

  // Append theme-specific font links (support multiple fonts per theme)
  if (themeFonts[themeName]) {
    themeFonts[themeName].forEach(fontHref => {
      appendLinkElement({ rel: 'stylesheet', href: fontHref });
    });
  }
}


const useContextTheme = () => {

  const {
    setClipToBounds,
    setShowHeat,
    setShouldIncludeTags
  } = mapStore((state) => state);

  const {
    tagsAuto,
    tagsCurrent,
    setTagsCurrent,
    setShowOnlyEventTags
  } = filterStore((state) => state)

  const {
    cityCurrent
  } = locationStore((state) => state)

  const {
    pageCurrent,
    themeName,
    setThemeName,
    themeObject,
    setShouldLeavePage,
    setShowLogin,
    setThemeObject,
    setThemeClass,
  } = pageStore((state) => state)

  const {
    navItems,
    listPrimary,
    partnerDetails,
    setNavItems
  } = userStore((state) => state);

  const mainTheme = themeName
  const whichTheme = themeName ? themeName : 'default'

  // Once theme is set, update the global state
  const themeObjectDep = JSON.stringify(themeObject)
  const memoizedThemeObject = useMemo(() => themeObject, [themeObject]);


  // Set global routing rules
  const openLinkFromCard = themeObject?.cards?.open_to_website_from_card
  const openLinkToWebsite = themeObject?.cards?.open_to_website == true || themeObject?.cards?.open_to_page != null
  const isMapCard = pageCurrent.includes('map')

  // REVIEW: cosolidates logic from
  // components/map/mapLayout.js
  // components/post/blocks/mapBlock/cards.jsx
  // components/elements/cards/card.jsx

  useEffect(() => {
    // Check if the user should leave the page
    if (openLinkFromCard || openLinkToWebsite) {
        const openNonMapCard = !isMapCard && openLinkToWebsite
        const leavePage = openLinkFromCard || openNonMapCard
        setShouldLeavePage(leavePage)
    }
  }, [openLinkFromCard, openLinkToWebsite, isMapCard])


  // Update the map based on the theme
  useEffect(() => {
    if (themeObject?.map?.showHeat !== undefined) {
      setShowHeat(themeObject.map.showHeat);
    }

    if (themeObject?.map?.shouldIncludeTags !== undefined) {
      setShouldIncludeTags(themeObject.map.shouldIncludeTags);
    }

    if (themeObject?.showLogin !== undefined) {
      setShowLogin(themeObject.showLogin)
    }

    if (themeObject?.events?.showOnlyEventTags !== undefined) {
      setShowOnlyEventTags(themeObject.events.showOnlyEventTags)
    }

    if (themeObject?.events?.link) {

      const updatedItems = navItems.map(item => {
        if (item.title === 'Calendar of Events') {
          item.path = themeObject?.events?.link
        }
        if (item.title === 'Share via Newsletter') {
          const themeArg = `${ themeName ? '&theme=' + themeName : ''}`
          item.path = themeObject?.events?.link_newsletter
            ? themeObject?.events?.link_newsletter
            : themeObject?.events?.link
              ? themeObject?.events?.link + '?compact=1' + themeArg
              : '?compact=1' + themeArg
        }

        if (item.title === 'Directory Map') {
          item.path = themeObject?.map?.link || item.path
        }

        if (item.title === 'Directory List') {
          item.path = themeObject?.directory?.link || item.path
        }

        // Make sure theme is set in all links
        if (item.path.includes(themeName) == false) {
          item.path = item.path + `?theme=${themeName}`
        }
        return item
      })

      setNavItems(updatedItems)
    } else {
      // If there's a city or theme set it in all nav items
      const citySlug = cityCurrent?.slug
      const themeName = whichTheme || 'default'
      if (citySlug && themeName) {
        const updatedItems = updateNavItemsWithCityAndTheme(navItems, citySlug, themeName, listPrimary)
        setNavItems(updatedItems)
      }
    }

    if (themeObject?.events?.eventLayout) {
      pageStore.setState({ eventLayout: themeObject.events.eventLayout })
    }

    const tagsAutoCombined = [
      ...(themeObject?.events?.tagAuto || []),
      ...(tagsAuto || [])
    ]
    if (tagsAutoCombined) {
      const tagsTheme = [
        ...tagsCurrent,
        ...tagsAutoCombined.filter(tag => !tagsCurrent.includes(tag))
      ]

      setTagsCurrent(tagsTheme)
    }

    if (themeObject?.places?.tagAuto) {
      const tagsTheme = [
        ...tagsCurrent,
        ...themeObject.places.tagAuto.filter(tag => !tagsCurrent.includes(tag))
      ]

      setTagsCurrent(tagsTheme)
    }

  }, [
    whichTheme,
    themeObjectDep
  ])

  // Get the current theme and merge with default
  const hasHyphen = whichTheme.includes('-')
  const currentTheme = themes[whichTheme]
    ? themes[whichTheme]
    : hasHyphen
      ? themes[whichTheme.replace('-', '_')] // Fix cases like 'downtown-tulsa'
      : themes.default


  const themeTokens = {
    ...themes.default,
    ...currentTheme
  }

  useEffect(() => {
    const themeTokens = themes[whichTheme] || themes.default
    const themeChanged = JSON.stringify(themeTokens) !== JSON.stringify(themeObject)
    if (themeChanged && themeTokens) {
      setThemeObject(themeTokens)
      setThemeClass(`theme-${whichTheme}`)
    }

    // Load fonts
    loadFonts(whichTheme)

  }, [whichTheme]);


  //setThemeName(whichTheme)
  //setThemeObject(themeTokens)

  return {
    themeClass: `theme-${whichTheme}`,
    theme: themeTokens
  }
}

export default useContextTheme